import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { WebsiteAuditService } from '../website-audit.service';
import { SidenavService } from 'src/app/sidenav/sidenav.service';
import { DarkmodeService } from 'src/app/services/darkmode.service';
import { SeoService } from 'src/app/seo/seo.service';
import { mapOpportunityRows } from '../opportunities/opportunities.utils';

const ACCENT = '#6161ff';
const COMP_COLOR = '#e0a544';

// How many rows the Overview's "Top hidden potentials" table shows.
const TOP_POTENTIALS_LIMIT = 5;
const COUNT_WORDS = ['no', 'one', 'two', 'three', 'four', 'five'];

// Categories that map onto the new overview.scores / sections blocks.
const CAT_ORDER = [
  'ux',
  'cr',
  'behavioralScience',
  'seo',
  'geo',
  'technical',
  'diagnostic',
  'agenticEcommerce',
];
const CAT_LABELS: { [k: string]: string } = {
  ux: 'UX',
  cr: 'Conversion Readiness',
  behavioralScience: 'Behavioral Science',
  seo: 'SEO',
  geo: 'GEO',
  technical: 'Technical Foundations',
  diagnostic: 'Site Health',
  agenticEcommerce: 'AI Shopping Readiness',
};
// Which report tab a category belongs to, matching the tab order in
// website-audit.component.ts: Overview, Opportunities, Experience,
// Discoverability, Technical Foundations, Behavioral Science, Audits.
const CAT_TAB_INDEX: { [k: string]: number } = {
  ux: 2,
  cr: 2,
  seo: 3,
  geo: 3,
  agenticEcommerce: 3,
  technical: 4,
  diagnostic: 4,
  behavioralScience: 5,
};
const CAT_COLORS: { [k: string]: string } = {
  ux: '#6161ff',
  cr: '#c98ae0',
  seo: '#5b9df0',
  geo: '#5fcf95',
  technical: '#e0a544',
  diagnostic: '#e26a5d',
  agenticEcommerce: '#4da18c',
};

// Distinct colors for each tracked competitor, so multiple competitors read as
// clearly separate series. COMP_COLOR (amber) stays first to match the
// single-competitor look.
const COMP_PALETTE = [
  '#e0a544',
  '#5b9df0',
  '#c98ae0',
  '#5fcf95',
  '#e26a5d',
  '#4da18c',
  '#dd7bb0',
  '#c8e6a0',
];

// Categories whose scale is inverted (a HIGHER number is WORSE) — the color,
// status, delta direction and competitor comparison all flip for these.
// Diagnostics used to be inverted, but it now reads higher-is-better like every
// other category, so nothing is inverted currently.
const INVERTED_SCORE_KEYS: string[] = [];

@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['../audit-theme.css', './overview.component.css'],
})
export class OverviewComponent implements OnInit, OnDestroy {
  constructor(
    private sidenavService: SidenavService,
    private websiteAuditService: WebsiteAuditService,
    private darkmodeService: DarkmodeService,
    private seoService: SeoService,
  ) {}

  // Shared read-only view — hide mutating actions like "Add competitor".
  public isShare = window.location.href.includes('/share/website-audit');

  // Booking CTA — same meeting link as the shared audit page: the rep can be
  // overridden with ?hubspot=, otherwise it falls back to the default owner.
  private hubspotName =
    new URLSearchParams(window.location.search).get('hubspot') || '';

  private subs: Subscription = new Subscription();
  // Overlay shown while the sub-bar swaps competitors.
  public competitorSwitching$ =
    this.websiteAuditService.getCompetitorSwitching();

  // colors exposed for the template
  accent = ACCENT;
  compColor = COMP_COLOR;

  // semantic colors — theme-aware (soft mint reads well on dark, deeper green on light)
  green = '#5fcf95';
  red = '#e26a5d';
  amber = '#e0a544';
  gray = '#6b6660';

  private isDark = true;

  private applyThemeColors(isDark: boolean) {
    this.isDark = isDark;
    if (isDark) {
      this.green = '#5fcf95';
      this.red = '#e26a5d';
      this.amber = '#e0a544';
      this.gray = '#6b6660';
    } else {
      this.green = '#16a34a';
      this.red = '#d1483b';
      this.amber = '#b9791a';
      this.gray = '#6b7280';
    }
  }

  // states
  public isLoading = false;
  public showLoader = false;
  public noData = false;
  public loaderPercentage = 0;
  public loaderLineIndex = 0;
  public loaderTexts: string[] = [
    'Initializing audit...',
    'Collecting site data...',
    'Reviewing structure...',
    'Checking key elements...',
    'Analyzing performance signals...',
    'Evaluating overall quality...',
    'Identifying potential issues...',
    'Cross-checking results...',
    'Compiling insights...',
    'Finalizing audit overview...',
  ];

  public selectedTask: any;
  public overviewData: any = null;
  private sectionsData: any = null;

  // competitors
  public competitors: any[] = [];
  public activeCompetitor: any = null;

  // header/sub-bar
  public periodMain = '';
  public periodCompare = '';

  // executive summary
  public summaryHeadline = '';
  public summaryText = '';
  public kpiHealth = 0;
  public kpiHealthDelta: number | null = null;
  public kpiHealthSpark: number[] = [];
  public kpiHealthStatus = '';
  public kpiHealthStatusColor = '';
  public kpiRank = 0;
  public kpiRankTotal = 0;
  public kpiRankNote = '';
  // strongest advantage — summary.strongestAdvantage
  public hasAdvantage = false;
  public advArea = '';
  public advDiffText = '';
  public advColor = '';
  public advNote = '';

  // top hidden potentials (first rows of the Opportunities roll-up)
  public topPotentials: any[] = [];
  public topPotentialsDesc = '';

  // one credible strength
  public credibleStrength = '';

  // category snapshot — one diverging bar per category (vs the tracked
  // competitor), or a plain score bar when nothing is benchmarked.
  public catCards: any[] = [];
  public catBars: any[] = [];
  public catBarsDiverging = false;
  public catBarsNote = '';

  // SEO/GEO scores come from a different API (keywords summary), not the audit
  // payload. SEO = general_obj.traditional_search (SERP), GEO = avg of the AI
  // engine scores (gpt/gemini). Cache the general objects so competitor
  // switches / theme toggles re-derive without refetching.
  private seoGeoScores: {
    [k: string]: {
      target: number | null;
      competitor: number | null;
      targetDelta: number | null;
    } | null;
  } = { seo: null, geo: null };
  private targetGeneral: any = null;
  private compGeneralCache: { [domain: string]: any } = {};
  private seoGeoTaskId = '';

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.subs.add(
      this.darkmodeService.getDarkMode().subscribe((isDark: boolean) => {
        // Follows the service value everywhere, including the shared page — the
        // share shell sets the theme (from the shared-with theme) via the
        // darkmode service, so charts/colors match the sharer's theme.
        this.applyThemeColors(!!isDark);
        if (this.overviewData) {
          this.hydrate(
            this.overviewData,
            this.sectionsData,
            this.competitors,
            this.activeCompetitor,
          );
        }
      }),
    );

    this.subs.add(
      this.websiteAuditService.getSelectedAuditTask().subscribe((res: any) => {
        if (res) {
          this.noData = false;
          this.selectedTask = res;
          this.hydrate(
            res.overview,
            res.sections,
            res.availableCompetitors || [],
            res.selectedCompetitor || null,
          );
          this.updateLoaderState();
        } else {
          this.resetEmpty();
        }
      }),
    );

    this.subs.add(
      this.websiteAuditService
        .getAuditTasksLoading()
        .subscribe((loading: any) => {
          this.isLoading = loading;
        }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ─── Hydration ───────────────────────────────────────────────────────────────

  private hydrate(
    overview: any,
    sections: any,
    competitors: any[],
    activeComp: any,
  ) {
    this.overviewData = overview || null;
    this.sectionsData = sections || null;
    this.competitors = Array.isArray(competitors) ? competitors : [];
    this.activeCompetitor = activeComp;

    this.summaryHeadline = overview?.summary?.headline || '';
    this.summaryText = overview?.summary?.text || '';

    this.maybeFetchSeoGeo();
    this.applySeoGeoScores();

    this.buildPeriod();
    this.buildKpis();
    this.buildTopPotentials();
    this.buildCatCards();
    this.buildCredibleStrength();
  }

  // ─── SEO / GEO scores (keywords-summary API) ─────────────────────────────────

  // Target general_obj is fetched once per task; the competitor's general_obj
  // is fetched per competitor domain (from get-competitor-summary). Both carry
  // traditional_search / gpt_performance / gemini_performance on a 0–100 scale.
  private maybeFetchSeoGeo() {
    const domain = this.getDomain(
      this.selectedTask?.domain || this.selectedTask?.url || '',
    );
    if (!domain) return;

    const taskId = this.selectedTask?.taskId;
    if (taskId && this.seoGeoTaskId !== taskId) {
      this.seoGeoTaskId = taskId;
      this.targetGeneral = null;

      this.subs.add(
        this.seoService.getKeywordsData({ domain }).subscribe({
          next: (res: any) => {
            this.targetGeneral = res?.variables?.general_obj || null;
            this.applySeoGeoScores();
            this.buildCatCards();
          },
          error: () => {},
        }),
      );
    }

    const compDomain = this.getDomain(
      this.activeCompetitor?.domain || this.activeCompetitor?.url || '',
    );
    if (compDomain && !(compDomain in this.compGeneralCache)) {
      this.compGeneralCache[compDomain] = null; // mark in-flight
      this.subs.add(
        this.seoService
          .getCompetitorSummary(
            { domain: compDomain },
            this.isShare ? compDomain : undefined,
          )
          .subscribe({
            next: (res: any) => {
              this.compGeneralCache[compDomain] = res?.general_obj || {};
              this.applySeoGeoScores();
              this.buildCatCards();
            },
            error: () => {
              this.compGeneralCache[compDomain] = {};
            },
          }),
      );
    }
  }

  private applySeoGeoScores() {
    const compDomain = this.getDomain(
      this.activeCompetitor?.domain || this.activeCompetitor?.url || '',
    );
    const compGeneral = compDomain ? this.compGeneralCache[compDomain] : null;

    this.seoGeoScores['seo'] = this.buildSeoGeoEntry('seo', compGeneral);
    this.seoGeoScores['geo'] = this.buildSeoGeoEntry('geo', compGeneral);
  }

  private buildSeoGeoEntry(key: string, compGeneral: any) {
    if (!this.targetGeneral) return null;
    return {
      target: this.scoreFromGeneral(key, this.targetGeneral),
      competitor: compGeneral ? this.scoreFromGeneral(key, compGeneral) : null,
      targetDelta: null,
    };
  }

  private numOrNull(v: any): number | null {
    // Scores are shown as whole numbers (e.g. 55.32 → 55).
    return typeof v === 'number' && !isNaN(v) ? Math.round(v) : null;
  }

  // SEO → SERP score; GEO → mean of the available generative-engine scores.
  private scoreFromGeneral(key: string, general: any): number | null {
    if (!general) return null;
    if (key === 'seo') return this.numOrNull(general.traditional_search);

    const parts = [general.gpt_performance, general.gemini_performance]
      .map((v) => this.numOrNull(v))
      .filter((v): v is number => v !== null);
    if (!parts.length) return null;
    return parts.reduce((a, b) => a + b, 0) / parts.length;
  }

  private resetEmpty() {
    this.noData = true;
    this.overviewData = null;
    this.sectionsData = null;
    this.competitors = [];
    this.activeCompetitor = null;
    this.catCards = [];
    this.catBars = [];
    this.topPotentials = [];
    this.credibleStrength = '';
    this.summaryHeadline = '';
    this.summaryText = '';
  }

  private targetSeries(): any {
    return (this.overviewData?.scoreHistory?.series || []).find(
      (s: any) => s.type === 'target',
    );
  }

  private buildPeriod() {
    const dates: string[] = this.overviewData?.scoreHistory?.dates || [];
    if (dates.length) {
      this.periodMain = this.fmtDate(dates[dates.length - 1]);
      this.periodCompare =
        dates.length > 1 ? `vs ${this.fmtDate(dates[dates.length - 2])}` : '';
    } else {
      this.periodMain = this.getAuditDateLabel();
      this.periodCompare = '';
    }
  }

  private buildKpis() {
    const scores = this.overviewData?.scores || {};
    const overall = scores.overall || {};
    const tSeries = this.targetSeries();

    this.kpiHealth =
      overall.target !== null && overall.target !== undefined
        ? Math.round(overall.target)
        : 0;
    this.kpiHealthDelta = this.roundDelta(overall.targetDelta);
    this.kpiHealthSpark = (tSeries?.scores?.overall || []).filter(
      (v: any) => v !== null && v !== undefined,
    );
    this.kpiHealthStatus = this.impactLabel(this.kpiHealth);
    this.kpiHealthStatusColor = this.impactColor(this.kpiHealth);

    // Market position: you vs all tracked competitors by overall score. Use each
    // competitor's latest series value; fall back to the single selected
    // competitor for one-time audits without per-competitor series.
    const compScores = this.enrichedCompetitors()
      .map((c) => this.seriesLatestOverall(c.series))
      .filter((v): v is number => v !== null);
    if (
      !compScores.length &&
      overall.competitor !== null &&
      overall.competitor !== undefined
    ) {
      compScores.push(overall.competitor);
    }

    if (compScores.length) {
      const target = overall.target || 0;
      this.kpiRankTotal = compScores.length + 1;
      this.kpiRank = 1 + compScores.filter((s) => s > target).length;
      const single = compScores.length === 1;
      if (this.kpiRank === 1) {
        const diff = Math.round(target - Math.max(...compScores));
        this.kpiRankNote = single
          ? `${diff} ahead of ${this.getActiveCompetitorName()}`
          : `${diff} ahead of nearest competitor`;
      } else {
        const diff = Math.round(Math.max(...compScores) - target);
        this.kpiRankNote = single
          ? `${diff} behind ${this.getActiveCompetitorName()}`
          : `${diff} behind the leader`;
      }
    } else {
      this.kpiRankTotal = 1;
      this.kpiRank = 1;
      this.kpiRankNote = 'No competitor tracked';
    }

    this.buildAdvantage();
  }

  // Strongest advantage KPI — the category where you lead by the most, straight
  // from summary.strongestAdvantage.
  private buildAdvantage() {
    const adv = this.overviewData?.summary?.strongestAdvantage;
    const area = adv?.area || '';
    this.hasAdvantage = !!area;
    if (!this.hasAdvantage) {
      this.advArea = '';
      this.advDiffText = '';
      this.advColor = '';
      this.advNote = '';
      return;
    }

    const diff = this.roundDelta(adv?.difference);
    const comp = this.getActiveCompetitorName();
    this.advArea = area;
    this.advColor =
      diff === null || diff === 0
        ? this.gray
        : diff > 0
          ? this.green
          : this.red;
    this.advDiffText = diff === null ? '' : `${diff > 0 ? '+' : ''}${diff}`;
    this.advNote = comp
      ? `vs ${comp} on the same benchmark`
      : 'on the same benchmark';
  }

  // The first rows of the Opportunities roll-up, rendered with the same table
  // as the Opportunities tab so the summary and the full list stay consistent.
  private buildTopPotentials() {
    const rows = mapOpportunityRows(
      this.sectionsData?.opportunities?.items,
      this.getActiveCompetitorName(),
    );
    this.topPotentials = rows.slice(0, TOP_POTENTIALS_LIMIT);

    const n = this.topPotentials.length;
    const word = COUNT_WORDS[n] || `${n}`;
    this.topPotentialsDesc =
      n === 1
        ? 'The highest-priority opportunity this period, ranked by evidence and expected commercial direction.'
        : `The ${word} highest-priority opportunities this period, ranked by evidence and expected commercial direction.`;
  }

  private buildCredibleStrength() {
    this.credibleStrength =
      this.overviewData?.summary?.oneCredibleStrength?.text || '';
  }

  private buildCatCards() {
    const scores = this.overviewData?.scores || {};
    const sections = this.sectionsData || {};
    const tSeries = this.targetSeries();

    // Always show the full known set (UX, CR, SEO, GEO, Technical, Diagnostics,
    // Agentic Ecommerce); categories the API doesn't return yet render as
    // "No data".
    const keys = [
      ...CAT_ORDER,
      ...Object.keys(scores).filter(
        (k) => k !== 'overall' && !CAT_ORDER.includes(k),
      ),
    ];

    this.catCards = keys.map((key) => {
      let s = scores[key] || {};
      // SEO/GEO aren't in the audit payload — merge in the keywords-summary
      // scores (target + selected competitor) fetched separately.
      if (
        (s.target === null || s.target === undefined) &&
        this.seoGeoScores[key]
      ) {
        s = { ...s, ...this.seoGeoScores[key] };
      }
      const sec = sections[key] || {};
      // Diagnostics: higher = more errors = worse, so good/bad reads inverted.
      const inverted = this.isInverted(key);
      const hasScore = s.target !== null && s.target !== undefined;
      const score = hasScore ? Math.round(s.target) : null;
      const delta = this.roundDelta(s.targetDelta);
      // A rising score is "good" normally, but "bad" for an inverted metric
      // (more errors). The arrow still shows the numeric direction; only the
      // color conveys good/bad.
      const deltaGood =
        delta === null ? null : inverted ? delta < 0 : delta > 0;
      const deltaColor =
        deltaGood === null ? this.gray : deltaGood ? this.green : this.red;
      const comp = s.competitor;
      const hasComp = comp !== null && comp !== undefined;
      const diff =
        hasScore && hasComp ? (score as number) - Math.round(comp) : null;
      // "Ahead" = the better side — the higher score normally, the lower one
      // (fewer errors) for an inverted metric.
      const ahead = diff === null ? null : inverted ? diff <= 0 : diff >= 0;
      const statusColor = hasScore
        ? this.impactColor(score as number, inverted)
        : this.gray;

      const spark = (tSeries?.scores?.[key] || []).filter(
        (v: any) => v !== null && v !== undefined,
      );
      // A flat series (every point identical) conveys no trend — hide the
      // sparkline entirely for those cards (#12).
      const sparkFlat = spark.length > 1 && new Set(spark).size === 1;
      // Color the sparkline by its OWN drawn direction (up = good → green for a
      // normal category), so it reflects the trend even when the API sends no
      // delta. Falls back to the delta color when there's no usable series.
      let sparkColor = deltaColor;
      if (spark.length > 1 && !sparkFlat) {
        const trend = spark[spark.length - 1] - spark[0];
        const trendGood = inverted ? trend < 0 : trend > 0;
        sparkColor =
          trend === 0 ? this.gray : trendGood ? this.green : this.red;
      }

      return {
        key,
        label: sec?.label || CAT_LABELS[key] || this.humanizeKey(key),
        tabIndex: CAT_TAB_INDEX[key] ?? null,
        failed: !hasScore,
        score,
        status: hasScore
          ? this.impactLabel(score as number, inverted)
          : 'No data',
        statusColor,
        statusBg: this.rgba(statusColor, 0.13),
        delta,
        deltaColor,
        deltaArrow:
          delta === null ? '' : delta > 0 ? '▲' : delta < 0 ? '▼' : '→',
        deltaText:
          delta === null ? '' : `${delta > 0 ? '+' : ''}${delta} vs last`,
        issues: sec?.issueCounts?.targetCurrent ?? 0,
        spark,
        sparkFlat,
        sparkColor,
        hasComp,
        comp: hasComp ? Math.round(comp) : null,
        // Signed gap vs the competitor — what the category snapshot plots.
        diff,
        ahead,
        // A competitor is tracked but this category has no competitor score —
        // i.e. the competitor couldn't be crawled for it.
        compMissing: hasScore && !!this.activeCompetitor && !hasComp,
        vsColor: ahead === null ? this.gray : ahead ? this.green : this.red,
        vsArrow: ahead === null ? '' : ahead ? '▲' : '▼',
        vsText:
          diff === null
            ? ''
            : `${Math.abs(diff)}${ahead ? ' ahead of ' : ' behind '}${this.getActiveCompetitorName()}`,
      };
    });

    this.buildCatBars();
  }

  // Category snapshot: one row per category. With a competitor tracked the bars
  // diverge from a centre line by the score gap (green ahead / red behind);
  // without one they fall back to plain 0–100 score bars.
  private buildCatBars() {
    const cards = this.catCards.filter((c) => !c.failed);
    const compName = this.getActiveCompetitorName();
    this.catBarsDiverging = cards.some((c) => c.diff !== null);

    if (this.catBarsDiverging) {
      // Scale to the widest gap so the largest bar fills its half of the track;
      // the floor keeps small gaps from looking dramatic.
      const maxAbs = Math.max(10, ...cards.map((c) => Math.abs(c.diff ?? 0)));
      this.catBars = cards.map((c) => {
        const diff = c.diff;
        const width = diff === null ? 0 : (Math.abs(diff) / maxAbs) * 50;
        const positive = (diff ?? 0) >= 0;
        return {
          key: c.key,
          label: c.label,
          tabIndex: c.tabIndex,
          // Bars grow out of the 50% centre line, left for behind, right for ahead.
          left: diff === null ? 50 : positive ? 50 : 50 - width,
          width,
          color: c.ahead === null ? this.gray : c.ahead ? this.green : this.red,
          valueText: diff === null ? 'n/a' : `${diff > 0 ? '+' : ''}${diff}`,
          valueColor:
            c.ahead === null ? this.gray : c.ahead ? this.green : this.red,
          tooltip:
            diff === null
              ? `No competitor score for ${c.label}`
              : `${c.label}: ${c.score} vs ${c.comp} (${compName})`,
        };
      });
      this.catBarsNote = compName
        ? `${this.selectedTask?.domain?.replace('www.', '')} vs ${compName}`
        : 'diverging';
      return;
    }

    this.catBars = cards.map((c) => ({
      key: c.key,
      label: c.label,
      tabIndex: c.tabIndex,
      left: 0,
      width: c.score,
      color: c.statusColor,
      valueText: `${c.score}`,
      valueColor: c.statusColor,
      tooltip: `${c.label}: ${c.score}/100`,
    }));
    this.catBarsNote = 'score out of 100';
  }

  // Every tracked competitor from the history series, with a stable key, display
  // label and color (assigned by position so a competitor keeps its color even
  // when others are toggled off).
  private enrichedCompetitors(): {
    series: any;
    key: string;
    label: string;
    color: string;
  }[] {
    const comps = (this.overviewData?.scoreHistory?.series || []).filter(
      (s: any) => s?.type === 'competitor',
    );
    return comps.map((s: any, idx: number) => ({
      series: s,
      key:
        s?.competitorId ||
        this.getDomain(s?.domain || s?.url || s?.name || '') ||
        `comp-${idx}`,
      label:
        this.getDomain(s?.domain || s?.url || s?.name || '') ||
        `Competitor ${idx + 1}`,
      color: COMP_PALETTE[idx % COMP_PALETTE.length],
    }));
  }

  // Latest non-null overall score in a series (used for standings + ranking).
  private seriesLatestOverall(series: any): number | null {
    const arr = series?.scores?.overall || [];
    for (let i = arr.length - 1; i >= 0; i--) {
      const v = arr[i];
      if (typeof v === 'number' && !isNaN(v)) return v;
    }
    return null;
  }

  // ─── Loader ────────────────────────────────────────────────────────────────

  private updateLoaderState() {
    const status = (this.selectedTask?.status || '').toString().toLowerCase();
    const done = status === 'done' || status === 'finished';
    this.showLoader = !this.overviewData && !done && !!this.selectedTask;
    this.loaderPercentage = this.showLoader ? 40 : 100;
    this.loaderLineIndex = Math.max(
      0,
      Math.round(this.loaderPercentage / this.loaderTexts.length) - 1,
    );
  }

  // ─── Navigation ──────────────────────────────────────────────────────────────

  goToTabIndex(index: number | null) {
    if (index === null || index === undefined) return;
    this.sidenavService.updateActiveTabIndex(index);
  }

  addAudit() {
    sessionStorage.setItem('addAudit', 'true');
    this.sidenavService.updateActiveTabIndex(8);
  }

  openHubspot(): void {
    // if (this.selectedTask?.domain?.includes('marksandspencer')) {
    //   this.hubspotName = 'george-quicksmithconsulting';
    // }

    const url = `https://meetings.hubspot.com/${this.hubspotName || 'patrick-fischer1'}`;
    window.open(url, '_blank', 'noopener');
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  // One-time audits have no history, so trend charts are hidden for them.
  get isBiweekly(): boolean {
    return (this.selectedTask?.auditFrequency || '') === 'biweekly';
  }

  getActiveCompetitorName(): string {
    return this.getDomain(
      this.activeCompetitor?.domain || this.activeCompetitor?.url || '',
    );
  }

  getCompChipLabel(comp: any): string {
    return comp?.domain || this.getDomain(comp?.url || '');
  }

  // Last-resort label for a score key CAT_LABELS doesn't cover — split the
  // camelCase into words instead of shouting it ("behavioralScience" →
  // "Behavioral Science", not "BEHAVIORALSCIENCE").
  private humanizeKey(key: string): string {
    const words = (key || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
    if (!words) return '';
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  private catKeyFromLabel(label: string): string {
    const l = (label || '').toLowerCase();
    for (const k of Object.keys(CAT_LABELS)) {
      if (CAT_LABELS[k].toLowerCase() === l || k === l) return k;
    }
    return '';
  }

  // Whether a category's score scale is inverted (higher = worse), e.g.
  // Diagnostics, which counts errors/issues.
  private isInverted(key: string): boolean {
    return INVERTED_SCORE_KEYS.includes(key);
  }

  impactLabel(num: number, inverted = false): string {
    if (typeof num !== 'number') return 'No data';
    if (inverted) {
      if (num >= 80) return 'Critical';
      if (num >= 50) return 'Warning';
      return 'Good';
    }
    if (num < 50) return 'Critical';
    if (num < 80) return 'Warning';
    return 'Good';
  }
  impactColor(num: number, inverted = false): string {
    if (typeof num !== 'number') return this.gray;
    if (inverted) {
      if (num >= 80) return this.red;
      if (num >= 50) return this.amber;
      return this.green;
    }
    if (num < 50) return this.red;
    if (num < 80) return this.amber;
    return this.green;
  }
  impactClass(num: number): string {
    if (typeof num !== 'number') return '';
    if (num < 50) return 'critical';
    if (num < 80) return 'warning';
    return 'good';
  }

  private roundDelta(d: any): number | null {
    if (d === null || d === undefined) return null;
    return Math.round(d * 10) / 10;
  }

  rgba(hex: string, a: number): string {
    const h = (hex || '').replace('#', '');
    if (h.length < 6) return `rgba(107,102,96,${a})`;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  }

  getDomain(url: string): string {
    if (!url) return '';
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;
      return new URL(normalized).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }

  getAuditFrequencyLabel(): string {
    const freq = this.selectedTask?.auditFrequency;
    if (!freq) return 'ONE-TIME';
    return freq === 'biweekly' ? 'BIWEEKLY' : freq.toUpperCase();
  }

  getAuditDateLabel(): string {
    const date = this.selectedTask?.updatedAt || this.selectedTask?.createdAt;
    return date ? this.fmtDate(date) : '';
  }

  private fmtDate(date: string): string {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  }
  private fmtShort(date: string): string {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return date;
    }
  }
}
